import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout/Layout';
import { Card, Button, Select } from '../../components/UI';
import { dashboardAPI, leadsAPI, callLogsAPI, reportsAPI, pipelineAPI, productsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlineDownload, HiOutlineUsers, HiOutlineUserAdd, HiOutlinePhone,
  HiOutlinePhoneIncoming, HiOutlinePhoneMissedCall, HiOutlineTrendingUp,
  HiOutlineCalendar, HiOutlineCheckCircle, HiOutlineClock, HiOutlineFire,
  HiOutlineClipboardCheck, HiOutlineSwitchHorizontal, HiOutlineChartPie,
  HiOutlineExclamation, HiOutlineDocumentText, HiOutlineArrowRight, HiOutlineRefresh,
} from 'react-icons/hi';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, PieChart, Pie, Cell, BarChart, Bar, FunnelChart, Funnel, LabelList,
} from 'recharts';
import { useNavigate } from 'react-router-dom';

const StatCard = ({ title, value, icon, trend, trendValue, trendLabel = 'vs last week', color }) => {
  const colorClasses = {
    primary: 'from-primary-500 to-primary-600',
    indigo: 'from-indigo-500 to-indigo-600',
    info: 'from-blue-500 to-blue-600',
    success: 'from-green-500 to-green-600',
    danger: 'from-red-500 to-red-600',
    orange: 'from-orange-500 to-orange-600',
    warning: 'from-amber-500 to-amber-600',
  };
  const ringClasses = {
    primary: 'ring-primary-500/20',
    indigo: 'ring-indigo-500/20',
    info: 'ring-blue-500/20',
    success: 'ring-green-500/20',
    danger: 'ring-red-500/20',
    orange: 'ring-orange-500/20',
    warning: 'ring-amber-500/20',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm hover:shadow-md transition-all duration-200">
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md bg-gradient-to-br ${colorClasses[color] || colorClasses.primary} ring-4 ${ringClasses[color] || ringClasses.primary}`}>
          <span className="text-white">{icon}</span>
        </div>
        <div className="min-w-0">
          <p className="text-xs text-gray-500 font-medium truncate">{title}</p>
          <p className="text-xl font-bold text-gray-900 leading-tight">{value}</p>
          {trend && (
            <span className={`text-xs font-medium inline-flex items-center gap-1 ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
              {trend === 'up' ? '↑' : '↓'} {trendValue} <span className="text-gray-400 font-normal">{trendLabel}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const ALERT_STYLES = {
  danger: { bg: 'bg-red-50', border: 'border-red-100', icon: 'bg-red-500', iconColor: <HiOutlineExclamation className="w-5 h-5 text-white" /> },
  warning: { bg: 'bg-orange-50', border: 'border-orange-100', icon: 'bg-orange-500', iconColor: <HiOutlineFire className="w-5 h-5 text-white" /> },
  caution: { bg: 'bg-amber-50', border: 'border-amber-100', icon: 'bg-amber-500', iconColor: <HiOutlineClock className="w-5 h-5 text-white" /> },
  info: { bg: 'bg-indigo-50', border: 'border-indigo-100', icon: 'bg-indigo-500', iconColor: <HiOutlineDocumentText className="w-5 h-5 text-white" /> },
  success: { bg: 'bg-green-50', border: 'border-green-100', icon: 'bg-green-500', iconColor: <HiOutlineChartPie className="w-5 h-5 text-white" /> },
};

const AlertItem = ({ type = 'info', title, description, onClick }) => {
  const s = ALERT_STYLES[type] || ALERT_STYLES.info;
  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer hover:shadow-sm transition-shadow ${s.bg} ${s.border}`}
    >
      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${s.icon}`}>
        {s.iconColor}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-900 truncate">{title}</p>
        <p className="text-xs text-gray-500 truncate">{description}</p>
      </div>
      <HiOutlineArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
    </div>
  );
};

const PIE_COLORS = ['#3B82F6', '#10B981', '#8B5CF6', '#F97316', '#EC4899', '#6B7280'];

const Dashboard = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isInitialized } = useAuth();
  const [dateRange, setDateRange] = useState('week');
  const [source, setSource] = useState('all');
  const [product, setProduct] = useState('all');

  // ---- Data fetching ----

  const { data: statsData, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['dashboardStats', dateRange, source, product],
    queryFn: async () => {
      try {
        const response = await dashboardAPI.getStats({ range: dateRange, source, product });
        return response.data?.data || {};
      } catch (error) {
        return {};
      }
    },
    enabled: isAuthenticated && isInitialized,
  });

  const { data: leadsCounts, refetch: refetchLeadsCounts } = useQuery({
    queryKey: ['leadsCounts', dateRange, source, product],
    queryFn: async () => {
      try {
        const response = await leadsAPI.getCounts({ range: dateRange, source, product });
        return response.data?.data || {};
      } catch (error) {
        return {};
      }
    },
    enabled: isAuthenticated && isInitialized,
  });

  const { data: callStats, refetch: refetchCallStats } = useQuery({
    queryKey: ['callStats', 'app', dateRange, source, product],
    queryFn: async () => {
      try {
        const response = await callLogsAPI.getStatistics({ source: 'app', range: dateRange, leadSource: source, product });
        return response.data?.data || {};
      } catch (error) {
        return {};
      }
    },
    enabled: isAuthenticated && isInitialized,
  });

  const { data: stageStats, refetch: refetchStageStats } = useQuery({
    queryKey: ['stageStats', dateRange, source, product],
    queryFn: async () => {
      try {
        const response = await pipelineAPI.getStats({ range: dateRange, source, product });
        return response.data?.data || [];
      } catch (error) {
        return [];
      }
    },
    enabled: isAuthenticated && isInitialized,
  });

  // Daily Activity Trend — Leads Created / Connected Calls / Follow-ups Completed
  const { data: dailyActivityData = [], refetch: refetchDailyActivity } = useQuery({
    queryKey: ['dailyActivity', dateRange, source, product],
    queryFn: async () => {
      try {
        const response = await reportsAPI.getDailyCalls({ range: dateRange, source, product });
        // expected shape: [{ date, leadsCreated, connectedCalls, followUpsCompleted }, ...]
        return response.data?.data || [];
      } catch (error) {
        return [];
      }
    },
    enabled: isAuthenticated && isInitialized,
  });

  // Lead Source Performance (donut)
  const { data: leadSourceData = [], refetch: refetchLeadSources } = useQuery({
    queryKey: ['leadSourcePerformance', dateRange, source, product],
    queryFn: async () => {
      try {
        const response = await reportsAPI.getLeadSources({ range: dateRange, source, product });
        // expected shape: [{ name, value, color }, ...]
        return response.data?.data || [];
      } catch (error) {
        return [];
      }
    },
    enabled: isAuthenticated && isInitialized,
  });

  // CEO Alerts
  const { data: ceoAlerts = [], refetch: refetchAlerts } = useQuery({
    queryKey: ['ceoAlerts'],
    queryFn: async () => {
      try {
        const response = await dashboardAPI.getAlerts?.();
        // expected shape: [{ id, type, title, description, link }, ...]
        return response?.data?.data || [];
      } catch (error) {
        return [];
      }
    },
    enabled: isAuthenticated && isInitialized,
  });

  // Lost Leads by Reason (donut)
  const { data: lostLeadsData = [], refetch: refetchLostLeads } = useQuery({
    queryKey: ['lostLeadsByReason', dateRange, source, product],
    queryFn: async () => {
      try {
        const response = await reportsAPI.getLostReasonAnalysis?.({ range: dateRange, source, product });
        // expected shape: [{ name, value, color }, ...]
        return response?.data?.data || [];
      } catch (error) {
        return [];
      }
    },
    enabled: isAuthenticated && isInitialized,
  });

  // Pending Actions Overview (horizontal bars)
  const { data: pendingActionsData = [], refetch: refetchPendingActions } = useQuery({
    queryKey: ['pendingActionsOverview', dateRange, source, product],
    queryFn: async () => {
      try {
        const response = await dashboardAPI.getPendingActions?.({ range: dateRange, source, product });
        // expected shape: [{ name, value }, ...]
        return response?.data?.data || [];
      } catch (error) {
        return [];
      }
    },
    enabled: isAuthenticated && isInitialized,
  });

  // Fetch products for filter dropdown
  const { data: productsData = [] } = useQuery({
    queryKey: ['productsList'],
    queryFn: async () => {
      try {
        const response = await productsAPI.getAll({ active: true });
        return response.data?.data || [];
      } catch (error) {
        return [];
      }
    },
    enabled: isAuthenticated && isInitialized,
  });

  // Fetch all unique sources dynamically from database
  const { data: sourcesData = [] } = useQuery({
    queryKey: ['uniqueLeadSources'],
    queryFn: async () => {
      try {
        const response = await leadsAPI.getSources();
        return response.data?.data || [];
      } catch (error) {
        console.error('Error fetching sources:', error);
        return [];
      }
    },
    enabled: isAuthenticated && isInitialized,
  });

  // Source options for dropdown - dynamically loaded from database
  const sourceOptions = useMemo(() => {
    const options = [{ value: 'all', label: 'All Sources' }];
    if (sourcesData && sourcesData.length > 0) {
      sourcesData.forEach((source) => {
        if (source && source.trim() !== '') {
          options.push({ value: source, label: source });
        }
      });
    }
    return options;
  }, [sourcesData]);

  // Product options for dropdown
  const productOptions = useMemo(() => {
    const options = [{ value: 'all', label: 'All Products' }];
    if (productsData && productsData.length > 0) {
      productsData.forEach((product) => {
        options.push({ value: product._id, label: product.name });
      });
    }
    return options;
  }, [productsData]);

  // Lead Funnel Overview
  const funnelData = React.useMemo(() => {
    if (!stageStats || stageStats.length === 0) return [];
    const sorted = [...stageStats].sort((a, b) => (b.leadsCount || 0) - (a.leadsCount || 0));
    return sorted.map((s, idx) => ({
      name: s.name,
      value: s.leadsCount || 0,
      fill: s.color || PIE_COLORS[idx % PIE_COLORS.length],
    }));
  }, [stageStats]);

  const isLoading = statsLoading;

  const handleRefresh = () => {
    refetchStats();
    refetchLeadsCounts();
    refetchCallStats();
    refetchStageStats();
    refetchDailyActivity();
    refetchLeadSources();
    refetchAlerts();
    refetchLostLeads();
    refetchPendingActions();
    toast.success('Dashboard refreshed');
  };

  const handleExport = async () => {
    try {
      toast.loading('Generating report...');

      // Build CSV data from dashboard stats
      const csvRows = [];

      // Header row
      csvRows.push(['CEO Dashboard Report']);
      csvRows.push([`Date Range: ${dateRange}`]);
      csvRows.push([`Generated: ${new Date().toLocaleString()}`]);
      csvRows.push([]);

      // Summary stats
      csvRows.push(['KEY METRICS']);
      csvRows.push(['Metric', 'Value']);
      csvRows.push(['Total Leads', totalLeads]);
      csvRows.push(["Today's Leads", stats?.todaysLeads || 0]);
      csvRows.push(['Calls Today', stats?.callsToday || 0]);
      csvRows.push(['Connected Calls', callStats?.connected || 0]);
      csvRows.push(['Not Connected', callStats?.notConnected || 0]);
      csvRows.push(['Connection Rate', `${connectionRate}%`]);
      csvRows.push(['Follow-ups Due Today', stats?.followUpsDueToday || 0]);
      csvRows.push(['Follow-ups Completed', stats?.followUpsCompleted || 0]);
      csvRows.push(['Hot Leads', stats?.hotLeads || 0]);
      csvRows.push(['Converted Leads', convertedCount]);
      csvRows.push(['Conversion Rate', `${conversionRate}%`]);
      csvRows.push([]);

      // Lead Source Performance
      if (leadSourceData && leadSourceData.length > 0) {
        csvRows.push(['LEAD SOURCE PERFORMANCE']);
        csvRows.push(['Source', 'Leads', 'Calls', 'Connected', 'Converted', 'Lost']);
        leadSourceData.forEach((item) => {
          csvRows.push([item.name, item.value || item.leads || 0, item.calls || 0, item.connected || 0, item.converted || 0, item.lost || 0]);
        });
        csvRows.push([]);
      }

      // Daily Activity Trend
      if (dailyActivityData && dailyActivityData.length > 0) {
        csvRows.push(['DAILY ACTIVITY TREND']);
        csvRows.push(['Date', 'Leads Created', 'Connected Calls', 'Follow-ups Completed']);
        dailyActivityData.forEach((item) => {
          csvRows.push([item.date || item.fullDate, item.leadsCreated || item.leads || 0, item.connected || item.connectedCalls || 0, item.followUpsCompleted || 0]);
        });
        csvRows.push([]);
      }

      // Pipeline Stages
      if (stageStats && stageStats.length > 0) {
        csvRows.push(['PIPELINE STAGES']);
        csvRows.push(['Stage', 'Leads Count']);
        stageStats.forEach((stage) => {
          csvRows.push([stage.name, stage.leadsCount || stage.count || 0]);
        });
        csvRows.push([]);
      }

      // CEO Alerts
      if (ceoAlerts && ceoAlerts.length > 0) {
        csvRows.push(['CEO ALERTS']);
        csvRows.push(['Type', 'Title', 'Description']);
        ceoAlerts.forEach((alert) => {
          csvRows.push([alert.type, alert.title, alert.description]);
        });
      }

      // Convert to CSV string
      const csvContent = csvRows.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `dashboard-report-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.dismiss();
      toast.success('Report exported successfully');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to export report');
      console.error('Export error:', error);
    }
  };

  const stats = statsData || {};

  const totalLeads = stageStats?.length > 0
    ? stageStats.reduce((sum, s) => sum + (s.leadsCount || 0), 0)
    : (leadsCounts ? Object.values(leadsCounts).reduce((a, b) => a + (b || 0), 0) : 0);

  const convertedStage = stageStats?.find((s) => s.key === 'converted');
  const convertedCount = convertedStage?.leadsCount || leadsCounts?.converted || 0;
  const conversionRate = totalLeads > 0 ? ((convertedCount / totalLeads) * 100).toFixed(1) : '0.0';
  const connectionRate = callStats?.total > 0 ? ((callStats.connected / callStats.total) * 100).toFixed(1) : '0.0';

  const totalLostLeads = lostLeadsData.reduce((a, b) => a + (b.value || 0), 0);

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-6">

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">CEO DASHBOARD - OVERALL SALES REVIEW</h1>
            <p className="text-gray-500 text-sm mt-1">Overview of key sales performance metrics and insights</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Date Filter</label>
              <Select
                options={[
                  { value: 'week', label: 'This Week' },
                  { value: 'month', label: 'This Month' },
                  { value: 'quarter', label: 'This Quarter' },
                ]}
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-36"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Source Filter</label>
              <Select
                options={sourceOptions}
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="w-36"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Product Filter</label>
              <Select
                options={productOptions}
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                className="w-36"
              />
            </div>
            
            <Button
              variant="outline"
              size="sm"
              icon={<HiOutlineDownload className="w-4 h-4" />}
              onClick={handleExport}
            >
              Export
            </Button>
          </div>
        </div>

        {/* Stat cards row 1 */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <StatCard title="Total Leads" value={totalLeads.toLocaleString()} icon={<HiOutlineUsers className="w-5 h-5" />} color="indigo" trend="up" trendValue="14%" />
          <StatCard title="Today's Leads" value={stats?.todaysLeads || 0} icon={<HiOutlineUserAdd className="w-5 h-5" />} color="primary" trend="up" trendValue="12%" />
          <StatCard title="Calls Done Today" value={stats?.callsToday || 0} icon={<HiOutlinePhone className="w-5 h-5" />} color="info" trend="up" trendValue="9%" />
          <StatCard title="Connected Calls" value={callStats?.connected || 0} icon={<HiOutlinePhoneIncoming className="w-5 h-5" />} color="success" trend="up" trendValue="11%" />
          <StatCard title="Not Connected Calls" value={callStats?.notConnected || 0} icon={<HiOutlinePhoneMissedCall className="w-5 h-5" />} color="danger" trend="down" trendValue="5%" />
          <StatCard title="Connection Rate" value={`${connectionRate}%`} icon={<HiOutlineTrendingUp className="w-5 h-5" />} color="orange" trend="up" trendValue="6.2%" />
        </div>

        {/* Stat cards row 2 */}
        <div className="grid grid-cols-2 lg:grid-cols-7 gap-3">
          <StatCard title="Follow-ups Due Today" value={stats?.followUpsDueToday || 0} icon={<HiOutlineCalendar className="w-5 h-5" />} color="indigo" trend="down" trendValue="7%" />
          <StatCard title="Follow-ups Completed" value={stats?.followUpsCompleted || 0} icon={<HiOutlineCheckCircle className="w-5 h-5" />} color="success" trend="up" trendValue="16%" />
          <StatCard title="Pending Follow-ups" value={stats?.pendingFollowUps || leadsCounts?.follow_up || 0} icon={<HiOutlineClock className="w-5 h-5" />} color="orange" trend="up" trendValue="9%" />
          <StatCard title="Hot Leads" value={stats?.hotLeads || leadsCounts?.hot || 0} icon={<HiOutlineFire className="w-5 h-5" />} color="danger" trend="up" trendValue="13%" />
          <StatCard title="Demo Scheduled" value={stats?.demoScheduled || 0} icon={<HiOutlineClipboardCheck className="w-5 h-5" />} color="indigo" trend="up" trendValue="10%" />
          <StatCard title="Converted Leads" value={convertedCount} icon={<HiOutlineSwitchHorizontal className="w-5 h-5" />} color="success" trend="up" trendValue="15%" />
          <StatCard title="Conversion Rate" value={`${conversionRate}%`} icon={<HiOutlineChartPie className="w-5 h-5" />} color="warning" trend="up" trendValue="0.8%" />
        </div>


       {/* Charts + Alerts section */}
<div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4 items-stretch">

  {/* Left block: top 2-col row + bottom 3-col row */}
  <div className="space-y-4">

    {/* Top row: Daily Activity Trend + Lead Source Performance */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

      {/* Daily Activity Trend */}
      <Card padding="p-4 sm:p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Daily Activity Trend</h3>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={dailyActivityData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="leadsCreated" name="Leads Created" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="connectedCalls" name="Connected Calls" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="followUpsCompleted" name="Follow-ups Completed" stroke="#8B5CF6" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Lead Source Performance */}
      <Card padding="p-4 sm:p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Lead Source Performance</h3>
        <div className="relative">
          <ResponsiveContainer width="100%" height={210}>
            <PieChart>
              <Pie
                data={leadSourceData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={2}
                dataKey="value"
              >
                {leadSourceData.map((entry, index) => (
                  <Cell key={`source-${index}`} fill={entry.color || PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" style={{ top: '-25px' }}>
            <span className="text-xl font-bold text-gray-900">{totalLeads.toLocaleString()}</span>
            <span className="text-xs text-gray-500">Total Leads</span>
          </div>
        </div>
        <div className="mt-2 space-y-1.5">
          {leadSourceData.map((item, idx) => (
            <div key={item.name} className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 text-gray-600">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color || PIE_COLORS[idx % PIE_COLORS.length] }} />
                {item.name}
              </span>
              <span className="font-medium text-gray-900">
                {item.value} ({totalLeads > 0 ? ((item.value / totalLeads) * 100).toFixed(1) : '0.0'}%)
              </span>
            </div>
          ))}
        </div>
      </Card>
    </div>

    {/* Bottom row: Lost Leads + Pending Actions + Lead Funnel */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

      {/* Lost Leads by Reason */}
      <Card padding="p-4 sm:p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Lost Leads by Reason</h3>
        <div className="relative">
          <ResponsiveContainer width="100%" height={210}>
            <PieChart>
              <Pie
                data={lostLeadsData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={2}
                dataKey="value"
              >
                {lostLeadsData.map((entry, index) => (
                  <Cell key={`lost-${index}`} fill={entry.color || PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" style={{ top: '-25px' }}>
            <span className="text-xl font-bold text-gray-900">{totalLostLeads}</span>
            <span className="text-xs text-gray-500">Total Lost Leads</span>
          </div>
        </div>
        <div className="mt-2 space-y-1.5">
          {lostLeadsData.map((item, idx) => (
            <div key={item.name} className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 text-gray-600">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color || PIE_COLORS[idx % PIE_COLORS.length] }} />
                {item.name}
              </span>
              <span className="font-medium text-gray-900">
                {item.value} ({totalLostLeads > 0 ? ((item.value / totalLostLeads) * 100).toFixed(1) : '0.0'}%)
              </span>
            </div>
          ))}
        </div>
      </Card>

      {/* Pending Actions Overview */}
      <Card padding="p-4 sm:p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Pending Actions Overview</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={pendingActionsData} layout="vertical" margin={{ left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
            <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
            <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} width={110} />
            <Tooltip />
            <Bar dataKey="value" name="Count" radius={[0, 3, 3, 0]} barSize={16}>
              {pendingActionsData.map((entry, index) => (
                <Cell key={`pending-${index}`} fill={entry.color || PIE_COLORS[index % PIE_COLORS.length]} />
              ))}
              <LabelList dataKey="value" position="right" fontSize={11} fill="#374151" />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Lead Funnel Overview */}
      <Card padding="p-4 sm:p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Lead Funnel Overview</h3>
        {funnelData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <HiOutlineUsers className="w-10 h-10 text-gray-300 mb-2" />
            <p className="text-gray-500 text-sm">No funnel data available</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <FunnelChart>
              <Tooltip />
              <Funnel dataKey="value" data={funnelData} isAnimationActive>
                <LabelList
                  position="left"
                  dataKey="name"
                  fill="#374151"
                  stroke="none"
                  fontSize={12}
                />
                <LabelList
                  position="center"
                  dataKey="value"
                  fill="#fff"
                  stroke="none"
                  fontSize={13}
                  fontWeight={600}
                />
              </Funnel>
            </FunnelChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  </div>

  {/* Right block: CEO Alerts — full height of both rows */}
  <Card padding="p-4 sm:p-6" className="h-full flex flex-col">
    <h3 className="font-semibold text-gray-900 mb-4">CEO Alerts</h3>
    <div className="space-y-2.5 flex-1">
      {ceoAlerts.length > 0 ? (
        ceoAlerts.slice(0, 5).map((alert) => (
          <AlertItem
            key={alert.id}
            type={alert.type}
            title={alert.title}
            description={alert.description}
            onClick={() => alert.link && navigate(alert.link)}
          />
        ))
      ) : (
        <p className="text-sm text-gray-400 text-center py-6">No alerts right now</p>
      )}
    </div>
    <button
      onClick={() => navigate('/app/alerts')}
      className="flex items-center gap-1 text-sm font-medium text-primary-600 mt-3"
    >
      View all alerts <HiOutlineArrowRight className="w-3.5 h-3.5" />
    </button>
  </Card>
</div>

       
      </div>
    </Layout>
  );
};

export default Dashboard;